import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Gem, Landmark, Leaf, Award, ArrowRight } from 'lucide-react';
import Header from './components/Header21';
import Footer from './components/Footer21';

const About21 = () => {
    const navigate = useNavigate();

    const values = [
        {
            icon: Gem,
            title: 'Artisanship',
            desc: 'Every stitch tells a story. Our master artisans bring generations of craft knowledge to each creation, ensuring unparalleled quality and attention to detail.'
        },
        {
            icon: Landmark,
            title: 'Heritage',
            desc: 'Rooted in the rich textile traditions of India, we honour time-honoured techniques while reimagining them for the modern connoisseur.'
        },
        {
            icon: Leaf,
            title: 'Sustainability',
            desc: 'We are committed to ethical sourcing, sustainable materials, and fair-trade practices that respect both artisan communities and the environment.'
        },
        {
            icon: Award,
            title: 'Excellence',
            desc: 'From concept to creation, we pursue perfection relentlessly. Each piece undergoes rigorous quality assessment before reaching your hands.'
        },
    ];

    const milestones = [
        { year: '2010', title: 'The Genesis', desc: 'Founded in a small Mumbai atelier with a vision to redefine Indian luxury fashion for the contemporary world.' },
        { year: '2013', title: 'Debut Collection', desc: 'Launched our inaugural couture line at Lakme Fashion Week, earning critical acclaim and a devoted following.' },
        { year: '2016', title: 'International Debut', desc: 'Showcased at Paris Fashion Week and expanded to serve discerning clientele across 15 countries.' },
        { year: '2019', title: 'The Sustainability Pledge', desc: 'Committed to 100% sustainable sourcing and launched our heritage handloom revival programme.' },
        { year: '2022', title: 'Digital Atelier', desc: 'Launched our immersive digital platform, bringing the bespoke experience to clients worldwide.' },
        { year: '2025', title: 'Global Recognition', desc: 'Honoured with the International Fashion Excellence Award, cementing our position as a global luxury brand.' },
    ];

    const stats = [
        { number: '10,000+', label: 'Happy Clients' },
        { number: '15+', label: 'Years of Craft' },
        { number: '500+', label: 'Bespoke Designs' },
        { number: '50+', label: 'Awards Won' },
    ];

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>
            <div style={{ fontFamily: "'DM Sans', -apple-system, sans-serif" }} className="min-h-screen bg-[#0A0A0A]">
                <Header />

                <main>
                    {/* Hero Section */}
                    <section className="relative w-full min-h-[60vh] lg:min-h-[75vh] overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/3965543/pexels-photo-3965543.jpeg?auto=compress&cs=tinysrgb&w=1260"
                            alt="Our Atelier"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0A]/80 via-[#0A0A0A]/50 to-[#0A0A0A]" />
                        <div className="relative z-10 h-full min-h-[60vh] lg:min-h-[75vh] flex items-center justify-center px-4">
                            <div className="text-center max-w-4xl">
                                <p className="text-[#C9A96E] text-sm tracking-[0.4em] uppercase mb-6">Our Legacy</p>
                                <h1 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-5xl lg:text-8xl font-light text-[#F5F0EB] mb-6 tracking-wide leading-tight">
                                    Our Story
                                </h1>
                                <p className="text-[#8A8A7A] text-lg lg:text-xl max-w-2xl mx-auto leading-relaxed">
                                    Where tradition meets contemporary vision, crafting timeless pieces that transcend generations.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Brand Story Section */}
                    <section className="py-16 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                                <div>
                                    <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">The Beginning</p>
                                    <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-5xl font-light text-[#F5F0EB] mb-8 leading-tight">
                                        A Vision Born of Passion
                                    </h2>
                                    <div className="space-y-5 text-[#8A8A7A] text-base lg:text-lg leading-relaxed">
                                        <p>
                                            Born from a deep reverence for India's incomparable textile heritage, our atelier was founded with an audacious
                                            vision: to create fashion that honours the past while embracing the future.
                                        </p>
                                        <p>
                                            Every thread we weave, every silhouette we sculpt, carries the weight of centuries-old craftsmanship infused with
                                            a modern sensibility that speaks to the discerning contemporary woman.
                                        </p>
                                        <p>
                                            What began as a singular dream in a small Mumbai workshop has blossomed into a globally recognised house of
                                            Indian couture, yet we remain faithful to the intimate, artisanal ethos that defines our identity.
                                        </p>
                                    </div>
                                </div>
                                <div>
                                    <blockquote className="border-l-2 border-[#C9A96E] pl-8 py-4">
                                        <p style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-2xl lg:text-3xl font-light text-[#C9A96E] italic leading-relaxed">
                                            "Fashion is not merely what you wear -- it is an intimate dialogue between heritage and the soul of the modern world."
                                        </p>
                                        <cite className="block mt-6 text-[#8A8A7A] text-sm tracking-wide not-italic">
                                            -- Founder & Creative Director
                                        </cite>
                                    </blockquote>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Founder / Designer Section */}
                    <section className="py-16 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                                <div className="order-2 lg:order-1">
                                    <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">The Visionary</p>
                                    <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-5xl font-light text-[#F5F0EB] mb-8 leading-tight">
                                        The Designer
                                    </h2>
                                    <div className="space-y-5 text-[#8A8A7A] text-base lg:text-lg leading-relaxed">
                                        <p>
                                            With an innate understanding of fabric, form, and the feminine silhouette, our founder brings over
                                            fifteen years of couture expertise to every creation. Trained under master weavers and drapers across
                                            Varanasi, Jaipur, and Paris, their work bridges the gap between Eastern artistry and Western sophistication.
                                        </p>
                                        <p>
                                            Their designs have graced the pages of Vogue India, Harper's Bazaar, and Elle, and have been worn by
                                            luminaries across the worlds of cinema, art, and international diplomacy.
                                        </p>
                                        <p>
                                            Beyond the atelier, they are a passionate advocate for preserving India's endangered textile crafts,
                                            working directly with weaver communities to ensure these ancient arts endure for generations to come.
                                        </p>
                                    </div>
                                </div>
                                <div className="order-1 lg:order-2">
                                    <div className="relative">
                                        <img
                                            src="https://images.pexels.com/photos/7147484/pexels-photo-7147484.jpeg?auto=compress&cs=tinysrgb&w=1260"
                                            alt="The Designer"
                                            className="w-full h-auto rounded-lg"
                                        />
                                        <div className="absolute inset-0 rounded-lg border border-[#C9A96E]/20" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Values Section */}
                    <section className="py-16 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-7xl mx-auto">
                            <div className="text-center mb-16">
                                <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">Our Philosophy</p>
                                <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-5xl font-light text-[#F5F0EB] mb-6">
                                    The Pillars We Stand On
                                </h2>
                                <p className="text-[#8A8A7A] text-lg max-w-2xl mx-auto">
                                    Four guiding principles that shape every creation and define our commitment to excellence.
                                </p>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {values.map((value, idx) => {
                                    const Icon = value.icon;
                                    return (
                                        <div
                                            key={idx}
                                            className="bg-[#141414] border border-[#2A2A2A] rounded-lg p-8 hover:border-[#C9A96E]/50 transition-all duration-500 group"
                                        >
                                            <div className="w-14 h-14 border border-[#C9A96E]/30 rounded-full flex items-center justify-center mb-6 group-hover:bg-[#C9A96E]/10 transition-colors duration-500">
                                                <Icon className="w-6 h-6 text-[#C9A96E]" />
                                            </div>
                                            <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-xl font-medium text-[#F5F0EB] mb-3">
                                                {value.title}
                                            </h3>
                                            <p className="text-[#8A8A7A] text-sm leading-relaxed">{value.desc}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>

                    {/* Timeline Section */}
                    <section className="py-16 lg:py-28 px-4 lg:px-12 border-b border-[#2A2A2A]">
                        <div className="max-w-5xl mx-auto">
                            <div className="text-center mb-16">
                                <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">Milestones</p>
                                <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-5xl font-light text-[#F5F0EB] mb-6">
                                    Our Journey
                                </h2>
                            </div>
                            <div className="relative">
                                {/* Vertical Line */}
                                <div className="absolute left-6 lg:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#C9A96E] via-[#C9A96E]/50 to-[#2A2A2A]" />

                                <div className="space-y-12">
                                    {milestones.map((milestone, idx) => (
                                        <div key={idx} className={`relative flex flex-col lg:flex-row items-start gap-6 lg:gap-12 ${idx % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}>
                                            {/* Year Marker */}
                                            <div className="absolute left-6 lg:left-1/2 transform -translate-x-1/2 w-3 h-3 bg-[#C9A96E] rounded-full border-2 border-[#0A0A0A] z-10" />

                                            {/* Content */}
                                            <div className={`flex-1 ml-14 lg:ml-0 ${idx % 2 === 0 ? 'lg:text-right lg:pr-16' : 'lg:text-left lg:pl-16'}`}>
                                                <span style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-2xl lg:text-3xl font-light text-[#C9A96E]">
                                                    {milestone.year}
                                                </span>
                                                <h3 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-xl lg:text-2xl font-medium text-[#F5F0EB] mt-2 mb-3">
                                                    {milestone.title}
                                                </h3>
                                                <p className="text-[#8A8A7A] leading-relaxed">{milestone.desc}</p>
                                            </div>

                                            {/* Spacer for alternating layout */}
                                            <div className="hidden lg:block flex-1" />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Atelier Image Section */}
                    <section className="relative w-full h-[50vh] lg:h-[60vh] overflow-hidden">
                        <img
                            src="https://images.pexels.com/photos/29256692/pexels-photo-29256692.jpeg?auto=compress&cs=tinysrgb&w=1260"
                            alt="The Atelier"
                            className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-[#0A0A0A]/60" />
                        <div className="relative z-10 h-full flex items-center justify-center px-4">
                            <div className="text-center max-w-3xl">
                                <p className="text-[#C9A96E] text-sm tracking-[0.4em] uppercase mb-4">Where Magic Happens</p>
                                <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-4xl lg:text-6xl font-light text-[#F5F0EB] tracking-wide">
                                    The Atelier
                                </h2>
                                <p className="text-[#F5F0EB]/70 text-lg mt-4 leading-relaxed">
                                    A sanctuary of creativity where the finest fabrics meet the most skilled hands,
                                    and where each garment begins its journey from vision to reality.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Stats Bar */}
                    <section className="py-16 lg:py-20 px-4 lg:px-12 bg-[#141414] border-y border-[#2A2A2A]">
                        <div className="max-w-7xl mx-auto">
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
                                {stats.map((stat, idx) => (
                                    <div key={idx} className="text-center">
                                        <div style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-5xl font-light text-[#C9A96E] mb-2">
                                            {stat.number}
                                        </div>
                                        <div className="text-[#8A8A7A] text-sm lg:text-base tracking-wide">{stat.label}</div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* CTA Section */}
                    <section className="py-20 lg:py-28 px-4 lg:px-12">
                        <div className="max-w-4xl mx-auto text-center">
                            <p className="text-[#C9A96E] text-sm tracking-[0.3em] uppercase mb-4">Begin Your Journey</p>
                            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }} className="text-3xl lg:text-5xl font-light text-[#F5F0EB] mb-6 leading-tight">
                                Experience the Art of Indian Couture
                            </h2>
                            <p className="text-[#8A8A7A] text-lg mb-10 leading-relaxed max-w-2xl mx-auto">
                                Discover our latest collections and become part of a legacy that celebrates craftsmanship, elegance, and the
                                timeless beauty of Indian fashion.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    onClick={() => navigate('/template21/shop')}
                                    className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-[#C9A96E] text-[#0A0A0A] font-semibold rounded-lg hover:bg-[#D4B87A] transition-colors"
                                >
                                    Explore Collections
                                    <ArrowRight size={18} />
                                </button>
                                <button
                                    onClick={() => navigate('/template21/contact')}
                                    className="inline-flex items-center justify-center gap-2 px-10 py-4 border border-[#C9A96E] text-[#C9A96E] font-semibold rounded-lg hover:bg-[#C9A96E] hover:text-[#0A0A0A] transition-colors"
                                >
                                    Contact Us
                                </button>
                            </div>
                        </div>
                    </section>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default About21;
